import React, { FC, ReactNode, CSSProperties, useEffect, useState } from 'react'
import { Select as AntdSelect, Form, SelectProps as AntdSelectProps } from 'antd'
import type { FormRule } from 'antd'
import type { DefaultOptionType } from 'antd/es/select'
import { useRules } from './hooks'
import { getFieldValueProps } from './utils'

// @ts-ignore
export interface SelectOption extends DefaultOptionType {
    value: string | number | boolean | null;
}

// @ts-ignore
interface SelectProps extends AntdSelectProps {
    name: string | string[]
    label?: string
    options: SelectOption[]
    disabled?: boolean
    style?: CSSProperties
    formItemStyle?: CSSProperties
    defaultValue?: string
    rules?: FormRule[]
    mode?: 'multiple' | 'tags'
    showSearch?: boolean
    defaultActiveFirstOption?: boolean
    suffixIcon?: ReactNode
    required?: boolean
}

const Select: FC<SelectProps> = ({
    name,
    label,
    options,
    disabled = false,
    style,
    formItemStyle,
    mode,
    onChange,
    onSearch,
    onBlur,
    showSearch = false,
    notFoundContent,
    defaultActiveFirstOption = true,
    suffixIcon,
    filterOption = true,
    open,
    tokenSeparators,
    required,
    rules = [],
    placeholder,
    ...rest
}) => {
    const form = Form.useFormInstance()
    const value = Form.useWatch(name, form)
    const [isDisabled, setIsDisabled] = useState(disabled)
    const [predefinedOptions, setPredefinedOptions] = useState<SelectOption[] | null>(null)
    const { allRules } = useRules({ required, rules })

    useEffect(() => {
        if (disabled !== undefined) {
            setIsDisabled(disabled)
        }
    }, [disabled])

    useEffect(() => {
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
            if (value.readOnly) {
                setIsDisabled(true)
            }
            form.setFieldValue(name, value.value)
        }
    }, [value])

    useEffect(() => {
        if (Array.isArray(value) && !open && mode === 'tags') {
            setPredefinedOptions(value.map(option => ({
                value: option,
                label: option,
            })))
        }
    }, [value, open, mode])

    return (
        <Form.Item
            getValueProps={getFieldValueProps}
            label={label}
            name={name}
            rules={allRules}
            {...(formItemStyle !== undefined && { style: formItemStyle })}
            {...rest}
        >
            <AntdSelect
                defaultActiveFirstOption={defaultActiveFirstOption}
                disabled={isDisabled}
                filterOption={filterOption}
                // @ts-ignore
                options={predefinedOptions || options}
                showSearch={showSearch}
                {...(mode !== undefined && { mode })}
                {...(notFoundContent !== undefined && { notFoundContent })}
                {...(onBlur !== undefined && { onBlur })}
                {...(onChange !== undefined && { onChange })}
                {...(onSearch !== undefined && { onSearch })}
                {...(open !== undefined && { open })}
                {...(placeholder !== undefined && { placeholder })}
                {...(style !== undefined && { style })}
                {...(suffixIcon !== undefined && { suffixIcon })}
                {...(tokenSeparators !== undefined && { tokenSeparators })}
            />
        </Form.Item>
    )
}

export default Select
